import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';

export type BrandUrl = {
  id: string;
  brand_id: string;
  name: string;
  url: string;
  display_order: number;
};

/** 生成フォーム用の軽量集約 (GET /brands/urls)。URL を持つ is_active ブランドのみ */
export type BrandWithUrls = {
  id: string;
  brand_name: string;
  urls: Pick<BrandUrl, 'id' | 'name' | 'url'>[];
};

export async function fetchBrandUrls(brandId: string): Promise<BrandUrl[]> {
  const { data } = await apiClient.get<{ urls?: BrandUrl[] }>(`/brands/${brandId}/urls`);
  return data.urls ?? [];
}

export async function createBrandUrl(brandId: string, payload: { name: string; url: string }): Promise<BrandUrl> {
  const { data } = await apiClient.post<{ url: BrandUrl }>(`/brands/${brandId}/urls`, payload);
  return data.url;
}

export async function updateBrandUrl(
  brandId: string,
  urlId: string,
  payload: Partial<{ name: string; url: string }>,
): Promise<BrandUrl> {
  const { data } = await apiClient.patch<{ url: BrandUrl }>(`/brands/${brandId}/urls/${urlId}`, payload);
  return data.url;
}

export async function deleteBrandUrl(brandId: string, urlId: string): Promise<void> {
  await apiClient.delete(`/brands/${brandId}/urls/${urlId}`);
}

export function useBrandUrls(brandId: string | undefined) {
  return useQuery({
    queryKey: ['brand-urls', brandId],
    queryFn: () => fetchBrandUrls(brandId!),
    enabled: !!brandId,
  });
}

export function useAllBrandUrls(enabled: boolean) {
  return useQuery({
    queryKey: ['all-brand-urls'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ brands?: BrandWithUrls[] }>('/brands/urls');
      return data.brands ?? [];
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5分
  });
}

/** ブランドURLとして許容される形式か (バックエンドの normalize_brand_url と同基準の緩い事前チェック) */
export function isValidBrandUrl(value: string): boolean {
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}
